import { useMemo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import moment from "moment-timezone";
import { useScheduleContext } from "../context/useScheduleContext";
import { useDefaultSection } from "./useDefaultSection";
import {
    calculateSectionsWithAutomaticBreaks,
    getAutomaticBreaks,
    getSectionsBounds
} from "../../helpers/form/sections";
import { calculateSections } from "./useInitialValues";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { useUser } from "../../../../../utils/hooks/user";
import type { Resource } from "../../types/Resource";

export const useFormValues = (form: UseFormReturn<any>) => {
    const loggedUser = useUser();
    const { defaultSection, breaks: defaultBreaks } = useDefaultSection();
    const { selectedCellOrEvent, type, resources } = useScheduleContext();

    const [isSimple, sections, recurrence, startDate, endDate] = useWatch({
        control: form.control,
        name: ["isSimple", "sections", "recurrence", "startDate", "endDate"]
    });

    return useMemo(() => {
        const eventDateSource = selectedCellOrEvent?.isTemplate
            ? moment(selectedCellOrEvent?.startDate)
            : moment(selectedCellOrEvent?.date || selectedCellOrEvent?.startDate);

        const _startDate = (startDate || selectedCellOrEvent?.startDate)
            ? combineDateAndTime(eventDateSource, moment(startDate || selectedCellOrEvent?.startDate))
            : undefined;

        const _endDate = (endDate || selectedCellOrEvent?.endDate)
            ? combineDateAndTime(eventDateSource, moment(endDate || selectedCellOrEvent?.endDate))
            : undefined;

        const end = moment(_endDate).isBefore(moment(_startDate), "seconds")
            ? moment(_endDate).add(1, "day")
            : _endDate;

        const duration = moment(end).diff(moment(_startDate), "seconds");

        let _sections = selectedCellOrEvent?.sections ||
            sections ||
            [
                {
                    ...defaultSection,
                    start: _startDate,
                    end,
                    duration: renderDurationAsFormat(duration, "HH:mm"),
                    canCalculateAutomaticBreaks: selectedCellOrEvent?.isCreating
                }
            ];

        const breaks =
            getAutomaticBreaks(
                (selectedCellOrEvent?.user as Resource)?.calculationGroup?.breaks,
                _sections,
                _sections?.[0]
            ) || [];

        if (breaks?.length > 0) {
            _sections = calculateSectionsWithAutomaticBreaks(_sections, breaks);
        }

        const subjectResource = ["home", "my-profile"].includes(type)
            ? loggedUser
            : type === "profile"
                ? resources?.[0]
                : undefined;

        const user = selectedCellOrEvent?.user
            ? (
                typeof selectedCellOrEvent?.user === "string"
                    ? { id: selectedCellOrEvent?.user }
                    : selectedCellOrEvent?.user
            ) : subjectResource;

        const _recurrence = selectedCellOrEvent?.recurrence || recurrence;
        const normalizedRecurrence = _recurrence?.endDate
            ? { ..._recurrence, endDate: moment(_recurrence.endDate).toDate() }
            : _recurrence;

        if (isSimple) {
            // The shift spans the widest range its sections cover: a section whose end was
            // pushed past the following sections' ends still belongs to the shift, and
            // reading the last row's end alone would shrink the shift back under it.
            const sectionsBounds = getSectionsBounds(_sections);

            const sectionBreaks = _sections
                ?.filter((s: any) => s.type === "break")
                ?.map((br: any) => ({
                    ...br,
                    start: typeof br.start === "number"
                        ? moment(_startDate).add(br.start, "seconds").toDate()
                        : br.start,
                    end: typeof br.end === "number"
                        ? moment(_startDate).add(br.end, "seconds").toDate()
                        : br.end
                }));

            return {
                isSimple,
                recurrence: normalizedRecurrence,
                ...(_sections ? _sections[0] : defaultSection),
                date: selectedCellOrEvent?.isTemplate
                    ? (
                        selectedCellOrEvent?.isCreating
                            ? moment(selectedCellOrEvent?.date).format("dddd").toLocaleLowerCase()
                            : selectedCellOrEvent?.date
                    ) : moment(selectedCellOrEvent?.date).toDate(),
                startDate: sectionsBounds.start
                    || (_startDate || defaultSection?.start ? moment(_startDate || defaultSection?.start).toDate() : undefined),
                endDate: sectionsBounds.end
                    || (_endDate || defaultSection?.end ? moment(_endDate || defaultSection?.end).toDate() : undefined),
                breaks: sectionBreaks?.length ? sectionBreaks : (breaks?.length ? breaks : defaultBreaks),
                users: user ? [user] : undefined
            };
        }

        return {
            isSimple,
            recurrence: normalizedRecurrence,
            date: selectedCellOrEvent?.isTemplate
                ? (
                    selectedCellOrEvent?.isCreating
                        ? moment(selectedCellOrEvent?.date).format("dddd").toLocaleLowerCase()
                        : selectedCellOrEvent?.date
                ) : moment(selectedCellOrEvent?.date).toDate(),
            sections: calculateSections({
                sections: _sections,
                date: selectedCellOrEvent?.isTemplate ? _startDate : selectedCellOrEvent?.date,
                startDate: _startDate
            }),
            users: user ? [user] : undefined
        };
    }, [selectedCellOrEvent, startDate, endDate, sections, defaultSection, isSimple, recurrence, defaultBreaks, type, resources, loggedUser]);
};
